import { useRef, useState, useCallback } from 'react';

/**
 * Tracks whether a form's state has changed since a modal opened, for
 * Modal's `confirmClose` prop — so closing (Escape, overlay click, ×)
 * asks before throwing away what was typed instead of doing it silently.
 *
 *   const dirty = useDirtyForm(form);
 *   <Modal confirmClose={dirty.isDirty} onClose={...} ...>
 *
 * `baseline` is captured once, the first time the hook sees a value
 * (i.e. when the modal opens with either a blank or a pre-filled
 * form) — every value after that is compared against IT, not against
 * the previous render, so typing a letter then deleting it again
 * correctly reports "not dirty".
 */
export default function useDirtyForm(value) {
  const baseline = useRef(undefined);
  const [, force] = useState(0);

  if (baseline.current === undefined) {
    baseline.current = JSON.stringify(value);
  }

  const isDirty = JSON.stringify(value) !== baseline.current;

  // Call after a successful save, or when a form modal is about to
  // close normally, so the NEXT time it opens starts fresh instead of
  // comparing against a stale baseline from the previous session.
  const reset = useCallback((nextValue) => {
    baseline.current = JSON.stringify(nextValue !== undefined ? nextValue : value);
    force((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isDirty, reset };
}
